import { useState } from 'react'
import { Badge, Card, CardContent } from '@atrvd/saas-ui'
import { ReadOnlyActionMenu } from '@/components/financial-accounts/ReadOnlyControls'
import { accountsAndCardsLabel } from '@/components/ledgersync/connectionManagement.utils'
import { DeleteManualBankDialog } from './DeleteManualBankDialog'
import { ManualBankFormDialog } from './ManualBankFormDialog'
import { MANUAL_BANK_COPY } from './manualBankCopy'
import type { ManualBankInScope } from './manualBanks.utils'

interface ManualBankDetailHeaderProps {
  /** The bank; `accounts` (the count) are the facility's on a facility page. */
  bank: ManualBankInScope
  /** An inactive facility's page: the kebab is shown disabled. */
  readOnly: boolean
  /** Called with the new name after the bank was saved from here. */
  onSaved: (institutionName: string) => void
  /** Called after the bank was deleted from here. */
  onDeleted: () => void
}

/**
 * Header card of a manual bank's detail page: name, "Manual" badge, account
 * count and the bank's Edit / Delete actions. The manual counterpart of
 * `ConnectionDetailHeader`, without logo, status, sync or import date.
 */
export function ManualBankDetailHeader({ bank, readOnly, onSaved, onDeleted }: ManualBankDetailHeaderProps) {
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const name = bank.institutionName

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-4 p-6">
        <div className="min-w-0 flex-1 basis-60">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="truncate text-xl font-bold tracking-tight">{bank.displayName}</h2>
            <Badge variant="secondary" className="px-3 py-[5px] text-[13px] text-slate-600">{MANUAL_BANK_COPY.sourceBadge}</Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {`${MANUAL_BANK_COPY.metaPrefix} · ${accountsAndCardsLabel(bank.accounts.length)}`}
          </p>
        </div>
        {/* No actions once the bank has no account left anywhere: there is
            nothing to rename or delete — it only stays on screen so an account
            can be added back. A page that merely lost its last LISTED one
            keeps them, since they act on the whole bank. */}
        {bank.bankAccountIds.length > 0 && (
          <ReadOnlyActionMenu
            readOnly={readOnly}
            ariaLabel="Financial account actions"
            className="h-[38px] w-[38px] border border-input bg-background [&>svg]:h-[17px] [&>svg]:w-[17px]"
            items={[
              // The unnamed group has no name to edit.
              ...(name !== null
                ? [
                    {
                      id: 'edit',
                      label: MANUAL_BANK_COPY.editBankCta,
                      onClick: () => setEditOpen(true),
                    },
                  ]
                : []),
              {
                id: 'delete',
                label: MANUAL_BANK_COPY.deleteBankCta,
                variant: 'danger' as const,
                onClick: () => setDeleteOpen(true),
              },
            ]}
          />
        )}
      </CardContent>

      {editOpen && name !== null && (
        <ManualBankFormDialog
          mode={{ kind: 'edit', institutionName: name }}
          onClose={() => setEditOpen(false)}
          onSaved={onSaved}
        />
      )}
      <DeleteManualBankDialog
        bank={deleteOpen ? bank : null}
        onClose={() => setDeleteOpen(false)}
        onDeleted={onDeleted}
      />
    </Card>
  )
}
